import { useContext, useEffect, useState, useRef } from "react";
import { AppContext } from "../App";
import logo from "../assets/logo.png";
import {
  MdClose,
  MdHome,
  MdInfoOutline,
  MdMail,
  MdPhoneInTalk,
  MdTerrain,
  MdTravelExplore,
  MdWaves,
  MdExplore,
} from "react-icons/md";
import { IoMdArrowDropdown } from "react-icons/io";
import { IoLogoWhatsapp } from "react-icons/io5";
import { dropdownContent } from "../data/menuDropdowns";

export default function SideBar() {
  const appContext = useContext(AppContext);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const dropdownRefs = useRef({});

  function closeSideBar() {
    appContext.setIsSideBarOpen(false);
  }

  function goTo(nav) {
    setActiveDropdown(null);
    appContext.navTo(nav);
    if (appContext.isSideBarOpen) {
      closeSideBar();
    }
  }

  function toggleDropdown(id, event) {
    event?.stopPropagation();
    setActiveDropdown((prev) => (prev === id ? null : id));
  }

  // Close dropdown when sidebar closes
  useEffect(() => {
    if (!appContext.isSideBarOpen) {
      setActiveDropdown(null);
    }
  }, [appContext.isSideBarOpen]);

  // Close dropdown when navigation changes
  useEffect(() => {
    setActiveDropdown(null);
  }, [appContext.navItem, appContext.navSubItem, appContext.navExtraItem, appContext.navMoreItem]);

  // Store refs for animation height calculation
  const setDropdownRef = (id, el) => {
    if (el) dropdownRefs.current[id] = el;
  };

  const items = [
    { id: "home", nav: { item: "home" }, label: "Home", icon: <MdHome size={20} /> },
    { id: "about", nav: { item: "about_us" }, label: "About Us", icon: <MdInfoOutline size={20} /> },
    { id: "adventure", nav: { item: "services", subItem: "adventure" }, label: "Adventure", icon: <MdExplore size={20} /> },
    { id: "safaris", nav: { item: "services", subItem: "safaris" }, label: "Safaris", icon: <MdTravelExplore size={20} /> },
    { id: "trekking", nav: { item: "services", subItem: "trekking" }, label: "Trekking", icon: <MdTerrain size={20} /> },
    { id: "zanzibar", nav: { item: "services", subItem: "zanzibar" }, label: "Zanzibar", icon: <MdWaves size={20} /> },
    { id: "contact", nav: { item: "contact_us" }, label: "Contact", icon: <MdPhoneInTalk size={20} /> },
    { id: "enquire", nav: { item: "plan-trip" }, label: "Enquire Now", icon: <MdMail size={20} /> },
  ];

  function isActive(item) {
    if (item.nav.item === "services" && item.nav.subItem) {
      return appContext.navItem === "services" && appContext.navSubItem === item.nav.subItem;
    }
    return !appContext.navItem ? item.nav.item === "home" : appContext.navItem === item.nav.item;
  }

  return (
    <aside className="SideBar" data-open={appContext.isSideBarOpen} aria-label="Sidebar navigation">
      <div
        className="body mNoScrollBar"
        style={{
          background: "linear-gradient(180deg, #f7fcfb 0%, #ecf5f2 100%)",
          padding: "14px 12px 16px",
        }}
      >
        {/* Header */}
        <div
          className="d-flex align-items-center justify-content-between"
          style={{
            border: "1px solid rgba(13, 59, 47, .12)",
            background: "#fff",
            borderRadius: 14,
            padding: "8px 10px",
            marginBottom: 12,
          }}
        >
          <button
            className="d-flex align-items-center"
            onClick={() => goTo({ item: "home" })}
            style={{
              border: "none",
              background: "transparent",
              gap: 8,
              padding: 0,
              textAlign: "left",
              cursor: "pointer",
            }}
          >
            <img src={logo} alt="Kwetu Africa Adventures logo" style={{ height: 34, width: "auto" }} />
            <span>
              <span className="font-semi-bold d-block" style={{ fontSize: 12.8, color: "#102132" }}>
                KWETU AFRICA
              </span>
              <span style={{ fontSize: 11.3, color: "#5f7082" }}>Adventures</span>
            </span>
          </button>

          <button
            type="button"
            onClick={appContext.closeSideBar}
            aria-label="Close menu"
            style={{
              border: "1px solid rgba(10,33,50,.2)",
              borderRadius: 10,
              width: 32,
              height: 32,
              display: "grid",
              placeItems: "center",
              background: "#fff",
              color: "#102132",
              cursor: "pointer",
            }}
          >
            <MdClose size={20} />
          </button>
        </div>

        {/* Promo Card */}
        <div
          style={{
            borderRadius: 14,
            background: "linear-gradient(120deg, #0c3028 0%, #0e3f33 100%)",
            padding: "12px",
            color: "#fff",
            marginBottom: 12,
          }}
        >
          <p className="mb-1 font-semi-bold" style={{ fontSize: 13.5 }}>Need a custom trip?</p>
          <p className="mb-2" style={{ fontSize: 12.5, color: "#c6d8e5", lineHeight: 1.45 }}>
            Speak with our local travel team in Arusha.
          </p>
          <a
            href="https://wa.me/255758319056"
            target="_blank"
            rel="noreferrer"
            className="d-inline-flex align-items-center font-semi-bold"
            style={{
              textDecoration: "none",
              borderRadius: 999,
              border: "1px solid rgba(242,196,94,.5)",
              background: "rgba(242,196,94,.14)",
              color: "#f2c45e",
              fontSize: 12.8,
              padding: "6px 10px",
              gap: 6,
              transition: "transform 0.2s ease",
            }}
            onMouseEnter={(e) => (e.currentTarget.style.transform = "scale(1.03)")}
            onMouseLeave={(e) => (e.currentTarget.style.transform = "scale(1)")}
          >
            <IoLogoWhatsapp size={14} />
            WhatsApp Us
          </a>
        </div>

        {/* Navigation */}
        <nav aria-label="Main">
          <div className="sidebar-nav-area" style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {items.map((item) => {
              const active = isActive(item);
              const hasDropdown = Boolean(dropdownContent[item.id]);
              const isDropdownActive = activeDropdown === item.id;
              const dropdownId = `dropdown-${item.id}`;

              return (
                <div key={item.id} style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                  {/* Main Nav Button */}
                  <button
                    type="button"
                    onClick={() => goTo(item.nav)}
                    onMouseEnter={() => hasDropdown && setActiveDropdown(item.id)}
                    data-active={active}
                    aria-expanded={hasDropdown ? isDropdownActive : undefined}
                    aria-controls={hasDropdown ? dropdownId : undefined}
                    style={{
                      border: active
                        ? "1px solid rgba(242,196,94,.64)"
                        : "1px solid rgba(13, 59, 47, .13)",
                      borderRadius: 12,
                      background: active
                        ? "linear-gradient(120deg, #0d3b2f 0%, #0f2639 100%)"
                        : "#fff",
                      color: active ? "#f2c45e" : "#173149",
                      padding: "10px 12px",
                      display: "flex",
                      alignItems: "center",
                      gap: 10,
                      justifyContent: "flex-start",
                      textAlign: "left",
                      boxShadow: active
                        ? "0 10px 22px rgba(11, 33, 44, .18)"
                        : "0 6px 14px rgba(8, 24, 42, .05)",
                      transition: "all 0.2s ease",
                      cursor: "pointer",
                    }}
                    onMouseOver={(e) => {
                      if (!active) {
                        e.currentTarget.style.boxShadow = "0 8px 18px rgba(8, 24, 42, .09)";
                        e.currentTarget.style.transform = "translateY(-1px)";
                      }
                    }}
                    onMouseOut={(e) => {
                      if (!active) {
                        e.currentTarget.style.boxShadow = "0 6px 14px rgba(8, 24, 42, .05)";
                        e.currentTarget.style.transform = "translateY(0)";
                      }
                    }}
                  >
                    {/* Icon */}
                    <span
                      style={{
                        width: 32,
                        height: 32,
                        borderRadius: 9,
                        display: "grid",
                        placeItems: "center",
                        background: active
                          ? "rgba(242,196,94,.18)"
                          : "rgba(13, 59, 47, .08)",
                        color: active ? "#f2c45e" : "#29546d",
                        flexShrink: 0,
                        transition: "background 0.2s ease",
                      }}
                    >
                      {item.icon}
                    </span>

                    {/* Label */}
                    <span style={{ minWidth: 0, display: "flex", alignItems: "center", flex: 1 }}>
                      <span
                        className="font-semi-bold d-block"
                        style={{ fontSize: 14, lineHeight: 1.2, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}
                      >
                        {item.label}
                      </span>
                    </span>

                    {/* Dropdown Arrow */}
                    {hasDropdown && (
                      <IoMdArrowDropdown
                        size={18}
                        style={{
                          marginLeft: "auto",
                          transition: "transform 200ms ease, color 0.2s ease",
                          transform: isDropdownActive ? "rotate(180deg)" : "none",
                          color: active ? "#f2c45e" : "#5f6c7a",
                          cursor: "pointer",
                          flexShrink: 0,
                        }}
                        onClick={(event) => toggleDropdown(item.id, event)}
                        aria-hidden="true"
                      />
                    )}
                  </button>

                  {/* Dropdown Panel */}
                  {hasDropdown && (
                    <div
                      id={dropdownId}
                      ref={(el) => setDropdownRef(item.id, el)}
                      className="sidebar-dropdown"
                      style={{
                        overflow: "hidden",
                        transition: "max-height 0.25s ease-out, opacity 0.2s ease",
                        maxHeight: isDropdownActive ? (dropdownRefs.current[item.id]?.scrollHeight || 300) + "px" : "0px",
                        opacity: isDropdownActive ? 1 : 0,
                        visibility: isDropdownActive ? "visible" : "hidden",
                      }}
                      role="region"
                      aria-labelledby={`btn-${item.id}`}
                    >
                      <div
                        style={{
                          background: "linear-gradient(135deg, #ffffff 0%, #f8fbfa 100%)",
                          border: "1px solid rgba(13, 59, 47, .1)",
                          borderRadius: "10px",
                          padding: "10px 12px",
                          marginTop: 4,
                          boxShadow: "0 4px 12px rgba(8, 24, 42, .06)",
                        }}
                      >
                        {/* Dropdown Title */}
                        <p
                          className="sidebar-dropdown-title"
                          style={{
                            fontSize: 11.5,
                            fontWeight: 600,
                            color: "#5f7082",
                            textTransform: "uppercase",
                            letterSpacing: "0.5px",
                            margin: "0 0 8px 0",
                            paddingLeft: 8,
                            borderLeft: "2px solid rgba(242,196,94,.6)",
                          }}
                        >
                          Explore {item.label}
                        </p>

                        {/* Dropdown Links */}
                        <div className="sidebar-dropdown-list" style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                          {dropdownContent[item.id].map((card, i) => (
                            <button
                              key={`${item.id}-${i}`}
                              type="button"
                              className="sidebar-dropdown-link"
                              onClick={() => goTo(card.nav)}
                              style={{
                                background: "transparent",
                                border: "none",
                                borderRadius: 8,
                                padding: "9px 12px 9px 36px",
                                textAlign: "left",
                                fontSize: 13.5,
                                color: "#2c4a5e",
                                cursor: "pointer",
                                display: "flex",
                                alignItems: "center",
                                gap: 8,
                                position: "relative",
                                transition: "all 0.15s ease",
                              }}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.background = "rgba(242,196,94,.12)";
                                e.currentTarget.style.color = "#0d3b2f";
                                e.currentTarget.style.paddingLeft = "40px";
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.background = "transparent";
                                e.currentTarget.style.color = "#2c4a5e";
                                e.currentTarget.style.paddingLeft = "36px";
                              }}
                              onFocus={(e) => {
                                e.currentTarget.style.outline = "2px solid rgba(242,196,94,.8)";
                                e.currentTarget.style.outlineOffset = "-2px";
                              }}
                              onBlur={(e) => {
                                e.currentTarget.style.outline = "none";
                              }}
                            >
                              {/* Decorative dot */}
                              <span
                                style={{
                                  position: "absolute",
                                  left: 16,
                                  width: 4,
                                  height: 4,
                                  borderRadius: "50%",
                                  background: "rgba(13, 59, 47, .3)",
                                  transition: "background 0.2s ease",
                                }}
                              />
                              {card.title}
                            </button>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </nav>
      </div>

      {/* Optional: Add CSS for smooth scrollbar if needed */}
      <style>{`
        .sidebar-dropdown::-webkit-scrollbar {
          width: 4px;
        }
        .sidebar-dropdown::-webkit-scrollbar-track {
          background: transparent;
        }
        .sidebar-dropdown::-webkit-scrollbar-thumb {
          background: rgba(13, 59, 47, .2);
          border-radius: 4px;
        }
        .sidebar-dropdown::-webkit-scrollbar-thumb:hover {
          background: rgba(13, 59, 47, .4);
        }
        @media (prefers-reduced-motion: reduce) {
          * {
            transition: none !important;
            animation: none !important;
          }
        }
      `}</style>
    </aside>
  );
}